'use client'

// BAO-125 Phase 5a: the one client-side path for reading and changing consent.
//
// Used by the banner, the footer "Your Privacy Choices" link, and the settings
// page toggles. A change: writes `bh_consent`, pushes the effective state into
// gtag / PostHog, notifies every listener (pixels, other instances) through a
// window event, and posts to `/api/v1/analytics/consent` so a signed-in
// admin's family row follows (a 401 for anonymous visitors is expected).
import { useCallback, useEffect, useState } from 'react'
import posthog from 'posthog-js'
import {
  type ConsentChoiceInput,
  type ConsentState,
  applyConsentSideEffects,
  consentCookieString,
  detectGpc,
  makeConsentRecord,
  readConsentState,
} from './consent'

export const CONSENT_CHANGED_EVENT = 'bh:consent-changed'
export const CONSENT_OPEN_EVENT = 'bh:open-consent'

function readFromDocument(): ConsentState {
  if (typeof document === 'undefined') return readConsentState('', false)
  return readConsentState(document.cookie, detectGpc(typeof navigator === 'undefined' ? undefined : navigator))
}

function runtime() {
  return {
    gtag: typeof window !== 'undefined' ? window.gtag : undefined,
    posthog: process.env.NEXT_PUBLIC_POSTHOG_KEY ? posthog : undefined,
  }
}

/**
 * Persist a choice everywhere. Only the categories the visitor actually
 * touched are sent to the server: a decision made on another device (a
 * stored analytics decline, say) must not be overwritten by this browser's
 * defaults. Exported for the settings page and tests.
 */
export function commitConsent(choice: ConsentChoiceInput): ConsentState {
  const gpc = detectGpc(typeof navigator === 'undefined' ? undefined : navigator)
  const current = readConsentState(document.cookie, gpc).effective
  const record = makeConsentRecord(choice, gpc, new Date(), current)
  try {
    document.cookie = consentCookieString(record, window.location.protocol === 'https:')
  } catch {
    // cookie write failures are non-fatal; the in-memory state still applies
  }
  const state = readConsentState(document.cookie, gpc)
  applyConsentSideEffects(state.effective, runtime())
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: state }))
  } catch {
    // no listeners is fine
  }
  // Best-effort persistence for signed-in admins; anonymous visitors get 401.
  try {
    void fetch('/api/v1/analytics/consent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...(choice.analytics !== undefined ? { analytics: record.analytics } : {}),
        ...(choice.ads !== undefined ? { ads: record.ads } : {}),
        gpc,
      }),
      keepalive: true,
    }).catch(() => {})
  } catch {
    // fetch unavailable (very old browsers) - nothing to do
  }
  return state
}

/** Reopen the banner from anywhere (footer link, settings). */
export function openConsentBanner(): void {
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT))
  } catch {
    // no banner mounted on this route
  }
}

export function useConsent() {
  // Server render and first client render agree on "undecided, defaults";
  // the real cookie is read after mount so hydration never mismatches.
  const [state, setState] = useState<ConsentState>(() => readConsentState('', false))
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    const current = readFromDocument()
    setState(current)
    setHydrated(true)
    // Bring gtag / PostHog in line with the cookie on every page load: the
    // PostHog opt-out is persisted by posthog-js, gtag needs the update each
    // document.
    applyConsentSideEffects(current.effective, runtime())
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<ConsentState>).detail
      setState(detail ?? readFromDocument())
    }
    window.addEventListener(CONSENT_CHANGED_EVENT, onChange)
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, onChange)
  }, [])

  const update = useCallback((choice: ConsentChoiceInput) => {
    setState(commitConsent(choice))
  }, [])

  return { state, hydrated, update }
}
